import Button from '@jetbrains/ring-ui/components/button/button'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import type {SerializedError} from '@reduxjs/toolkit'
import type {FetchBaseQueryError} from '@reduxjs/toolkit/src/query/fetchBaseQuery'
import {memo, useCallback, useRef} from 'react'

import useMyId from '../../../../../hooks/useMyId'
import {HTTPCodesEnum} from '../../../../../rest/errors/HTTPError'
import {ROOT_PROJECT_ID} from '../../../../../types'
import {base_uri} from '../../../../../types/BS_types'
import type {RepositoryGroup} from '../../services/pipelinesApi.types'
import {serializeError} from '../../utils/error'
import {isSpaceConnectionEnabled} from '../../utils/featureToggles'

import RepositoryList from './RepositoryList/RepositoryList'

import styles from './Repositories.css'

const TIMEOUT = 1000

type Props = {
  vcsType: string
  groups?: RepositoryGroup[] | null
  isFetching?: boolean
  isLoadingGroups?: boolean
  error?: FetchBaseQueryError | SerializedError | null
  refetch: () => void
  connectionId: string
  onSelect: (
    url: string,
    name: string,
    branch: string,
    isPrivate: boolean,
    connectionId: string,
    username?: string,
  ) => void
}

function Repositories({
  vcsType,
  connectionId,
  onSelect,
  refetch,
  groups,
  isLoadingGroups,
  isFetching,
  error,
}: Props) {
  const iframe = useRef<HTMLIFrameElement>(null)
  const myId = useMyId()

  const handleSelect = useCallback(
    (url: string, name: string, branch: string, isPrivate: boolean, username?: string) => {
      onSelect(url, name, branch, isSpaceConnectionEnabled || isPrivate, connectionId, username)
    },
    [connectionId, onSelect],
  )

  return (
    <div className={styles.repositories}>
      {isLoadingGroups ? (
        <LoaderInline>{'Loading repositories...'}</LoaderInline>
      ) : groups != null ? (
        groups.length > 0 ? (
          <RepositoryList repositoryGroups={groups} onSelect={handleSelect} />
        ) : (
          <div>{'There are no connected repositories.'}</div>
        )
      ) : error != null ? (
        'status' in error && error.status === HTTPCodesEnum.UNAUTHORIZED ? (
          <>
            {'To show available repositories TeamCity requires access to your GitHub account.'}
            <iframe title={`${vcsType} login`} hidden ref={iframe} />
            <div className={styles.actions}>
              <Button
                className={styles.button}
                primary
                onClick={() => {
                  const win = window.BS?.Util?.popupWindow(
                    `${base_uri}/oauth/${vcsType?.toLowerCase()}/repositories.html?projectId=${ROOT_PROJECT_ID}&connectionId=${connectionId}&updateToken=true&showMode=popup&userId=${myId}`,
                    `repositories_${connectionId}`,
                    {safe: false, opener: iframe.current?.contentWindow},
                  )
                  const interval = window.setInterval(() => {
                    if (win == null || win.closed) {
                      window.clearInterval(interval)
                      refetch()
                    }
                  }, TIMEOUT)
                }}
              >
                {`Sign in to ${vcsType}`}
              </Button>
              <Button className={styles.button} onClick={refetch}>
                {'Refresh'}
              </Button>
              {isFetching && <LoaderInline />}
            </div>
          </>
        ) : (
          <div className={styles.error}>{serializeError(error)}</div>
        )
      ) : null}
    </div>
  )
}

export default memo(Repositories)
